import Table from '../../table';
import Typography from '@/components/atoms/typography';
import { PDP_COMPONENTS_PRODUCT_CLICKED } from '@/constants/events';
import { availabilityColors, getAvailabilityStatus, warehouseLocations } from '@/constants/inventoryAvailability';
import { desktop, smallMobile, tablet } from '@/constants/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import { ArrowDownTrayIcon, EnvelopeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Modal from '../../modal';
import BundleProductImage from './bundle-product-image';
import { localeMapping } from '@/constants/localeMapping';
import useAccount from '@/lib/hooks/useAccount';
import useFormat from '@/hooks/useFormat';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface bundleAttributes {
  bundleData: any;
  assetsAttributes?: Array<{ productname: string; code: string; image: string; inventory: string }>;
  componentsInventory: any;
   warehouse:any;
  isPrototype?: boolean;
}
interface SelectedProduct {
  warehouses: { code: string; quantity: number }[];
  iscritical: boolean;
  availabilityStatus?: any;
}

const BundleProducts = ({ bundleData, componentsInventory, warehouse, isPrototype }: bundleAttributes) => {
  const warehouseName = warehouseLocations[warehouse];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile] = useMediaQuery(tablet);
  const [isDesktop] = useMediaQuery(desktop);
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const localeKey = localeMapping[localeString];
  const { account } = useAccount();
  const router = useRouter();
  const { formatCurrency } = useFormat();

  const handleOpenModal = (item: any) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };

  const bundleDataMap: Record<string, any> = {};
  if (Array.isArray(bundleData)) {
    bundleData.forEach((item) => {
      bundleDataMap[item.ItemCode] = item;
    });
  }
  const bundledProducts = (componentsInventory.length ? componentsInventory : bundleData)
    .map((inventoryItem: any) => {
      const bundleItem = bundleDataMap[inventoryItem.itemcode] || bundleDataMap[inventoryItem.ItemCode];
      if (!bundleItem) return null;
      const availabilityStatus = getAvailabilityStatus(inventoryItem.quantity);
      const backgroundColor = availabilityColors[availabilityStatus] || '#FFFFFF';
      return { ...inventoryItem, ...bundleItem, availabilityStatus, backgroundColor };
    })
    .filter((item: any) => item !== null)
    .sort((a: any, b: any) => (b?.Prices?.[0]?.value?.centAmount ?? 0) - (a?.Prices?.[0]?.value?.centAmount ?? 0));

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleClicked = (name: string, sku: string) => {
    // gtag('event', PDP_COMPONENTS_PRODUCT_CLICKED, 'Selected Product: ' + sku);
    if (typeof window !== 'undefined') {
      window.dataLayer = (window.dataLayer as any) || ([] as any);
      window.dataLayer.push({
        event: PDP_COMPONENTS_PRODUCT_CLICKED,
        'Selected Product: ': sku,
        userId: account?.accountId,
      });
    }
  };

  useEffect(() => {
    const body = document.body;
    if (isModalOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
    return () => {
      body.style.overflow = '';
    };
  }, [isModalOpen]);

  return (
    <div className="mt-[40px]">
      <Typography className="components-info !mb-4 md:!mb-[40px] text-[22px] !font-medium !leading-snug text-[#090909] md:text-[26px]">
        Components
      </Typography>
      <div>
        {!isDesktop ? (
          <>
            {Array.isArray(bundledProducts) &&
              bundledProducts?.map((item: any, index: number) => (
                <div className="border-b border-[#bbbbbc] py-3 flex gap-3" key={item?.id || item?.SKU || `bundle-${index}`}>
                  <BundleProductImage
                    item={item}
                    localeKey={localeKey}
                    href={`/${item?.Slug[localeKey]}/p/${item?.SKU}`}
                    onClick={() => handleClicked(item?.Name[localeKey], item?.SKU)}
                  />
                  <div className="flex flex-col flex-1 gap-1">
                    <Link href={`/${item?.Slug[localeKey]}/p/${item?.SKU}`}>
                      <div className="text-14 font-semibold leading-5 text-[#090909]" onClick={() => handleClicked(item?.Name[localeKey], item?.SKU)}>
                        {item?.Name[localeKey]}
                      </div>
                    </Link>
                    <div className="text-12 text-[#666666]">
                      Item Code: <span className="text-[#090909]">{item?.ItemCode}</span>
                    </div>
                    {!(locale === 'au' && item.Range === 'Basis' && item.isBuilder) && !isPrototype && (
                      <div className="text-12 text-[#666666]">
  RRP:{' '}
  <span className="text-[#090909]">
    {formatCurrency(
      item?.Prices[0]?.value?.centAmount / 100,
      item?.Prices[0]?.value?.currencyCode
    )}{' '}
    (inc. GST)
  </span>
</div>
                    )}
                    {item.availabilityStatus && !isPrototype ? (
                      <div className="mt-1">
                        {item.iscritical === false && (
                          <span
                            style={{
                              backgroundColor:
                                availabilityColors[
                                getAvailabilityStatus(
                                  item.warehouses?.find((wh: any) => wh.code === warehouse)?.quantity ?? 0
                                )
                                ] || '#FFFFFF',
                            }}
                            className="inline-flex items-center gap-1 rounded-full px-3 py-1"
                          >
                            {(() => {
                              const qty =
                                item.warehouses?.find((wh: any) => wh.code === warehouse)?.quantity ?? 0;

                              return qty === 'N/A' || Number.isNaN(Number(qty)) || Number(qty) <= 0
                                ? 'Out of Stock'
                                : Math.floor(Number(qty));
                            })()}
                          </span>
                        )}
                        {item.iscritical === false ? (
                          <div className="mt-1 flex gap-1">
                            <Typography fontSize={12} className="!text-12 text-[#666666]">
                              {`Stock availability in ${warehouseName} warehouse`}
                            </Typography>
                          </div>
                        ) : (
                          <>
                          <div className="mt-1 text-12 font-semibold text-red-500">
                            This product is in high demand.
                            </div>
                            <div className="text-12 font-semibold text-red-500">
                              Please{' '}
                            <Link href={`/${localeString}/contact-us`}><span className="font-semibold text-red-500 !underline">contact</span></Link>{' '}
                            our Team.
                          </div>
                          </>
                        )}
                      </div>
                    ) : (
                      !isPrototype ? (
                      <div className="mt-1 flex gap-1 text-12">
                        <span>Not available, please</span>
                        <Link href={`/${localeString}/contact-us`}><span className="lowercase text-[#29a1aa]">Contact us</span></Link>
                      </div>
                      ) : null
                    )}
                  </div>
                </div>
              ))}
          </>
        ) : (
          <div className="">
            <Table.Container className="!border-[#CCCCCC]  border-l-0 border-r-0">
              <Table.Head className="bg-white !normal-case">
                <tr className="border-b text-left [&>td]:border-b [&>td]:border-b-[#CCCCCC]">
                  <Table.Cell className='!w-[150px]' > </Table.Cell>
                  <Table.Cell className='!w-[600px]' >
                    <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                      Product Name
                    </Typography>
                  </Table.Cell>
                  <Table.Cell className='!w-[180px]' >
                    <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                      Item Code
                    </Typography>
                  </Table.Cell>
                  {!isPrototype && (
                    <Table.Cell className='!w-[180px]' >
                      <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                        RRP
                      </Typography>
                    </Table.Cell>
                  )}
                  {!isPrototype && (
                    <Table.Cell>
                      <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                        Availability
                      </Typography>
                    </Table.Cell>
                  )}
                </tr>
              </Table.Head>
              <Table.Body>
                {Array.isArray(bundledProducts) &&
                  bundledProducts?.map((item: any, index: number) => (
                    <Table.Row
                      key={item?.id as string}
                      className="whitespace-normal border-b border-gray-300 align-middle"
                    >
                      <Table.Cell className="w-20 !p-0">
                        <BundleProductImage
                          item={item}
                          localeKey={localeKey}
                          href={`/${item?.Slug[localeKey]}/p/${item?.SKU}`}
                          onClick={() => handleClicked(item?.Name[localeKey], item?.SKU)}
                          imgClassName="h-20 w-20 border-b border-white object-contain"
                        />
                      </Table.Cell>
                      <Table.Cell className="whitespace-normal">
                        <Link href={`/${item?.Slug[localeKey]}/p/${item?.SKU}`}>
                          <Typography
                            className="text-16 !font-medium leading-6 text-[#090909]"
                            onClick={() => handleClicked(item?.Name[localeKey], item?.SKU)}
                          >
                            {item?.Name[localeKey]}
                          </Typography>
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                          {item?.ItemCode}
                        </Typography>
                      </Table.Cell>
                      {!isPrototype && (
                        <Table.Cell>
                          {!(locale === 'au' && item.Range === 'Basis' && item.isBuilder) && (
                            <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                              {formatCurrency(
                                item?.Prices[0]?.value?.centAmount / 100,
                                item?.Prices[0]?.value?.currencyCode,
                              )}
                            </Typography>
                          )}
                        </Table.Cell>
                      )}
                      {!isPrototype && (
                        item.availabilityStatus ? (
                          <Table.Cell className="!py-3 !px-4">
                            {item.iscritical === false && (
                              <div
                              style={{
                                backgroundColor:
                                  availabilityColors[
                                  getAvailabilityStatus(
                                    item.warehouses?.find((wh: any) => wh.code === warehouse)?.quantity ?? 0
                                  )
                                  ] || "#FFFFFF",
                              }}
                              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
                            >
                              <span className="text-[12px] leading-4 text-[#090909]">
                                  {(() => {
                                    const qty =
                                      item.warehouses?.find((wh: any) => wh.code === warehouse)?.quantity ?? 0;

                                    return qty === 'N/A' || Number.isNaN(Number(qty)) || Number(qty) <= 0
                                      ? 'Out of Stock'
                                      : Math.floor(Number(qty));
                                  })()}
                              </span>

                            </div>
                            )}
                            {item.iscritical === false ? (
                              <div className="mt-1.5 flex gap-1">
                                <Typography
                                  fontSize={12}
                                  className="!text-[12px] !font-normal text-[#666666] leading-[16px]"
                                >
                                  {`Stock available in ${warehouseName} warehouse`}
                                </Typography>
                              </div>
                            ) : (
                              <>
                                <div className="mt-1.5 text-[12px] font-semibold text-red-500 leading-[16px]">
                                  This product is in high demand.
                                </div>
                                <div className="text-[12px] font-semibold text-red-500 leading-[16px]">
                                  Please{' '}
                                  <Link href={`/${localeString}/contact-us`}>
                                    <span className="font-semibold text-red-500 !underline">contact</span>
                                  </Link>{' '}
                                  our Team.
                                </div>
                              </>
                            )}
                          </Table.Cell>
                        ) : (
                          <Table.Cell className="!py-3 !px-4">
                            <div className="mt-1 flex gap-1 text-[12px] leading-[16px]">
                              <span>Not available, please</span>
                              <Link href={`/${localeString}/contact-us`}>
                                <span className="lowercase text-[#29a1aa]">Contact us</span>
                              </Link>
                            </div>
                          </Table.Cell>
                        )
                      )}
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Container>
          </div>
        )}
      </div>
      <Modal
        className="flex !h-auto flex-col !p-10"
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        preventScroll={false}
        closeButton={true}
        size="sm"
        variant="default"
        centered={true}
      >
        {!(selectedProduct?.availabilityStatus === 'Out of Stock') && (
          <div>
            <Typography>GWA Stock Levels</Typography>
          </div>
        )}
        <div className="mt-5 max-h-[20rem] overflow-y-auto">
          {Array.isArray(selectedProduct?.warehouses) &&
            selectedProduct?.warehouses.map((item: any) => (
              <div key={item.id} style={{ border: '1px solid gray' }} className="flex justify-between border px-4 py-2">
                <div className="text-14 font-normal">{warehouseLocations[item?.code]}</div>
                <div className="text-14 font-medium">{item?.quantity}</div>
              </div>
            ))}
        </div>
        {(selectedProduct?.iscritical || selectedProduct?.availabilityStatus === 'Out of Stock') && (
          <>
                          <div className="mt-1 text-12 font-semibold text-red-500">
                            This product is in high demand.
                            </div>
                            <div className="text-12 font-semibold text-red-500">
                              Please{' '}
                            <Link href={`/${localeString}/contact-us`}><span className="font-semibold text-red-500 !underline">contact</span></Link>{' '}
                            our Team.
                          </div>
                          </>
        )}
      </Modal>
    </div>
  );
};

export default BundleProducts;
